import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Swords, Shield, Trophy, TrendingUp, Award, Flame, Star } from "lucide-react";
import { rgba, DEFAULT_ACCENT } from "@/lib/accent";

/** Career + duel + war + signature moves + history panel for My Profile.
 *  Tinted with the viewer's accent color throughout. */
export default function LegacyPanel({ userId, accent = DEFAULT_ACCENT }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get(`/users/${userId}/legacy`)
      .then((r) => setData(r.data))
      .catch(() => setError("Couldn't load Legacy stats."))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) {
    return <div className="border rounded-sm p-6 text-sm text-[#A1A1AA]" style={{ borderColor: `${accent}33`, background: "#0A0A0A" }}>Loading Legacy…</div>;
  }
  if (error || !data) {
    return <div className="border border-[#27272A] bg-[#121212] rounded-sm p-6 text-sm text-[#E13C32]">{error || "No data"}</div>;
  }

  const { career, duel_record, team_war_contribution, signature_moves, history } = data;

  return (
    <section className="space-y-4" data-testid="legacy-panel">
      {/* Gamified section header */}
      <div className="flex items-center gap-2 mb-2">
        <div
          className="flex items-center justify-center w-6 h-6 rounded-sm shrink-0"
          style={{
            background: `linear-gradient(135deg, ${rgba(accent, 0.25)}, transparent)`,
            border: `1px solid ${accent}66`,
            boxShadow: `0 0 10px ${rgba(accent, 0.3)}`,
          }}
        >
          <Trophy className="w-3.5 h-3.5" style={{ color: accent }} />
        </div>
        <h2 className="font-display text-lg tracking-wider uppercase">Legacy</h2>
        <div
          className="hidden sm:block flex-1 h-px ml-1"
          style={{ background: `linear-gradient(90deg, ${accent}55, transparent)` }}
        />
      </div>

      {/* Career */}
      <Card title="CAREER" icon={Star} accent={accent}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Stat icon={TrendingUp} label="TOTAL SALES" value={career.total_sales} color={accent} />
          <Stat icon={TrendingUp} label="TOTAL LINES" value={career.total_lines.toLocaleString()} color={accent} />
          <Stat icon={Award} label="ALL-TIME XP" value={career.all_time_xp.toLocaleString()} color={accent} />
          <Stat icon={Flame} label={`LVL ${career.current_level}`} value={career.current_level_title} color={accent} isText />
        </div>
      </Card>

      {/* Duel + War */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card title="DUEL RECORD" icon={Swords} accent={accent}>
          <div className="grid grid-cols-2 gap-2">
            <Stat label="W – L" value={`${duel_record.wins} – ${duel_record.losses}`} color={accent} />
            <Stat label="CURRENT STREAK" value={duel_record.current_win_streak} color={accent} />
            <Stat label="LONGEST STREAK" value={duel_record.longest_win_streak} color={accent} />
            <Stat label="TOTAL DUELS" value={duel_record.wins + duel_record.losses} color={accent} />
          </div>
        </Card>
        <Card title="TEAM WARS" icon={Shield} accent={accent}>
          <div className="grid grid-cols-2 gap-2">
            <Stat label="PARTICIPATED" value={team_war_contribution.wars_participated} color={accent} />
            <Stat label="WON AS MEMBER" value={team_war_contribution.wars_won_as_member} color={accent} />
            <Stat label="LED" value={team_war_contribution.wars_led} color={accent} />
            <Stat label="LED WINS" value={team_war_contribution.wars_led_won} color={accent} />
          </div>
        </Card>
      </div>

      {/* Signature moves */}
      {(signature_moves?.biggest_sales?.length > 0 || signature_moves?.biggest_day) ? (
        <Card title="SIGNATURE MOVES" icon={Award} accent={accent}>
          <div className="space-y-2">
            {signature_moves.biggest_day ? (
              <SignatureRow
                title="Biggest single-day XP"
                sub={`${signature_moves.biggest_day._id} · ${signature_moves.biggest_day.sales} sales`}
                value={`${(signature_moves.biggest_day.xp || 0).toLocaleString()} XP`}
                color={accent}
              />
            ) : null}
            {signature_moves.biggest_sales.map((s, i) => (
              <SignatureRow
                key={s.id}
                title={`Top sale #${i + 1}`}
                sub={`${s.segment} · ${new Date(s.created_at).toLocaleDateString()}`}
                value={`${s.lines} lines`}
                color={accent}
              />
            ))}
          </div>
        </Card>
      ) : null}

      {/* History */}
      {history?.length > 0 ? (
        <Card title={`CHALLENGE HISTORY (${history.length})`} icon={Swords} accent={accent}>
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1">
            {history.map((h) => (
              <HistoryRow key={h.id} h={h} accent={accent} />
            ))}
          </div>
        </Card>
      ) : (
        <Card title="CHALLENGE HISTORY" icon={Swords} accent={accent}>
          <div className="text-xs text-[#A1A1AA] italic">No challenges yet. Head to Arena to start one.</div>
        </Card>
      )}
    </section>
  );
}

function Card({ title, icon: Icon, accent, children }) {
  return (
    <div
      className="relative rounded-lg p-4 overflow-hidden border-2"
      style={{
        borderColor: `${accent}55`,
        backgroundColor: "#0A0A0A",
        backgroundImage: `linear-gradient(160deg, ${rgba(accent, 0.08)}, transparent 60%)`,
        boxShadow: `0 0 14px ${rgba(accent, 0.15)}, inset 0 0 18px rgba(0,0,0,0.4)`,
      }}
    >
      {["top-0 left-0","top-0 right-0","bottom-0 left-0","bottom-0 right-0"].map((pos, i) => (
        <div
          key={i}
          className={`absolute ${pos} w-10 h-10 pointer-events-none`}
          style={{
            background: `linear-gradient(${i === 0 ? "135deg" : i === 1 ? "225deg" : i === 2 ? "45deg" : "-45deg"}, ${accent}77 0 1.5px, transparent 2.5px)`,
          }}
        />
      ))}
      <div className="relative flex items-center gap-2 mb-3">
        {Icon ? <Icon className="w-3.5 h-3.5" style={{ color: accent }} /> : null}
        <div className="font-label text-xs tracking-widest" style={{ color: accent }}>{title}</div>
        <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${accent}44, transparent)` }} />
      </div>
      <div className="relative">{children}</div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color = "#FFFFFF", isText = false }) {
  return (
    <div
      className="p-2 rounded-sm border border-white/10"
      style={{ background: "rgba(0,0,0,0.35)" }}
    >
      <div className="flex items-center gap-1.5 mb-0.5">
        {Icon ? <Icon className="w-3 h-3" style={{ color }} /> : null}
        <div className="font-label text-[9px] tracking-widest text-[#A1A1AA]">{label}</div>
      </div>
      <div
        className={isText ? "text-sm truncate" : "font-mono font-black text-xl leading-none truncate"}
        style={{ color, filter: `drop-shadow(0 0 6px ${rgba(color, 0.35)})` }}
      >
        {value}
      </div>
    </div>
  );
}

function SignatureRow({ title, sub, value, color }) {
  return (
    <div
      className="flex items-center justify-between p-2.5 rounded-sm border"
      style={{ borderColor: `${color}33`, background: "rgba(0,0,0,0.35)" }}
    >
      <div className="min-w-0">
        <div className="text-sm text-white truncate">{title}</div>
        <div className="text-[10px] text-[#A1A1AA] truncate">{sub}</div>
      </div>
      <div
        className="font-mono font-black text-lg leading-none shrink-0"
        style={{ color, filter: `drop-shadow(0 0 8px ${rgba(color, 0.45)})` }}
      >
        {value}
      </div>
    </div>
  );
}

function HistoryRow({ h, accent }) {
  const outcomeCls = h.outcome === "win" ? "#34C759" : h.outcome === "loss" ? "#E13C32" : "#A1A1AA";
  const outcomeLabel = h.outcome === "win" ? "W" : h.outcome === "loss" ? "L" : h.outcome === "tie" ? "T" : h.status.toUpperCase();
  return (
    <div
      className="flex items-center justify-between p-2 rounded-sm border border-white/10"
      style={{ background: "rgba(0,0,0,0.35)" }}
      data-testid={`history-row-${h.id}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {h.kind === "duel" ? (
          <Swords className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} />
        ) : (
          <Shield className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} />
        )}
        <div className="min-w-0">
          <div className="text-sm text-white truncate">vs {h.opponent_name}</div>
          <div className="text-[10px] text-[#A1A1AA] truncate">
            {h.type_label} · {h.completed_at ? new Date(h.completed_at).toLocaleDateString() : ""}
          </div>
        </div>
      </div>
      <div
        className="font-mono font-black text-lg w-6 text-right"
        style={{ color: outcomeCls, filter: `drop-shadow(0 0 6px ${rgba(outcomeCls, 0.5)})` }}
      >
        {outcomeLabel}
      </div>
    </div>
  );
}
